/**
 * Утилита для работы с часовыми поясами
 * Часовой пояс хранится в БД как глобальная настройка
 * Бэкенд сохраняет время в UTC
 * Фронтенд конвертирует UTC → часовой пояс пользователя
 */

let cachedTimezone = null;
const DEFAULT_TIMEZONE = 3;

export const TIMEZONE_CHANGED_EVENT = 'app-timezone-changed';

const normalizeTimezoneValue = (value) => {
  const parsed = parseInt(value ?? '', 10);
  return Number.isFinite(parsed) ? parsed : DEFAULT_TIMEZONE;
};

const persistTimezone = (value) => {
  const normalized = normalizeTimezoneValue(value);
  cachedTimezone = normalized;

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('user_timezone', String(normalized));
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TIMEZONE_CHANGED_EVENT, {
      detail: { timezone: normalized }
    }));
  }

  return normalized;
};

// Получаем часовой пояс (кэшируем после первой загрузки)
export const getUserTimezone = () => {
  if (cachedTimezone !== null) {
    return cachedTimezone;
  }
  if (typeof localStorage === 'undefined') {
    return DEFAULT_TIMEZONE;
  }
  return normalizeTimezoneValue(localStorage.getItem('user_timezone') || String(DEFAULT_TIMEZONE));
};

export const setUserTimezone = (value) => persistTimezone(value);

// Загружаем часовой пояс с сервера
export const loadTimezone = async () => {
  try {
    const token = localStorage.getItem('token');
    const API_URL = '';
    const response = await fetch(`${API_URL}/api/settings/timezone`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      return persistTimezone(data.value || String(DEFAULT_TIMEZONE));
    }
  } catch (error) {
    console.error('Ошибка загрузки часового пояса:', error);
  }
  return getUserTimezone();
};

/**
 * Конвертирует UTC дату в локальное время пользователя
 * @param {string} dateStr - Дата в формате ISO (UTC)
 * @param {boolean} includeTime - Включать ли время
 * @returns {string} Отформатированная дата
 */
export const formatTimezoneDate = (dateStr, includeTime = true) => {
  if (!dateStr) return '';
  
  const userTimezone = getUserTimezone();
  
  // Парсим дату. Если нет Z в конце - добавляем (значит это UTC)
  const isoStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  const utcDate = new Date(isoStr);
  
  // Получаем компоненты UTC
  const utcYear = utcDate.getUTCFullYear();
  const utcMonth = utcDate.getUTCMonth();
  const utcDay = utcDate.getUTCDate();
  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();
  const utcSeconds = utcDate.getUTCSeconds();
  
  // Создаём новую дату со смещением часового пояса
  const localDate = new Date(Date.UTC(utcYear, utcMonth, utcDay, utcHours + userTimezone, utcMinutes, utcSeconds));
  
  const day = localDate.getUTCDate().toString().padStart(2, '0');
  const month = (localDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = localDate.getUTCFullYear();
  
  if (!includeTime) {
    return `${day}.${month}.${year}`;
  }
  
  const hours = localDate.getUTCHours().toString().padStart(2, '0');
  const minutes = localDate.getUTCMinutes().toString().padStart(2, '0');
  const seconds = localDate.getUTCSeconds().toString().padStart(2, '0');
  
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
};

/**
 * Форматируем только время
 */
export const formatTimezoneTime = (dateStr) => {
  if (!dateStr) return '';
  
  const userTimezone = getUserTimezone();
  const isoStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  const utcDate = new Date(isoStr);
  
  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();
  
  const totalMinutes = (utcHours + userTimezone) * 60 + utcMinutes;
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
